import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Chip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  AccountTree,
  TrackChanges,
  Analytics,
  Star,
  TrendingUp,
  Schedule,
  EmojiEvents,
  Security,
  Psychology,
  Speed,
  CheckCircle,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import DebugAuth from '../components/DebugAuth';

const MotionBox = motion(Box);
const MotionCard = motion(Card);

const LandingPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const features = [
    {
      icon: <AccountTree sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: 'Talent Tree System',
      description: 'Visualize your personal development journey with an intuitive talent tree inspired by gaming.',
    },
    {
      icon: <Schedule sx={{ fontSize: 40, color: 'secondary.main' }} />,
      title: 'Time Allocation',
      description: 'Optimize your limited weekly time across different personal development goals.',
    },
    {
      icon: <Analytics sx={{ fontSize: 40, color: 'success.main' }} />,
      title: 'Progress Analytics',
      description: 'Track your progress with detailed analytics and insights into your development patterns.',
    },
    {
      icon: <EmojiEvents sx={{ fontSize: 40, color: 'warning.main' }} />,
      title: 'Achievement System',
      description: 'Unlock achievements and milestones as you progress through your personal development journey.',
    },
    {
      icon: <TrendingUp sx={{ fontSize: 40, color: 'info.main' }} />,
      title: 'Goal Tracking',
      description: 'Set, track, and achieve your personal development goals with our comprehensive system.',
    },
    {
      icon: <Security sx={{ fontSize: 40, color: 'error.main' }} />,
      title: 'Secure & Private',
      description: 'Your personal development data is encrypted and secure with enterprise-grade security.',
    },
  ];

  return (
    <Box sx={{ minHeight: '100vh' }}>
      {/* Hero Section */}
      <Container maxWidth="lg" sx={{ pt: 8, pb: 8 }}>
        <MotionBox
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          sx={{ textAlign: 'center', mb: 8 }}
        >
          {/* Hero Badge */}
          <MotionBox
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            sx={{ mb: 4 }}
          >
            <Chip
              label="🎮 Introducing Gamified Personal Development"
              variant="outlined"
              sx={{
                px: 3,
                py: 1,
                fontSize: '0.9rem',
                borderColor: 'primary.main',
                color: 'primary.main',
                backgroundColor: 'rgba(255, 215, 0, 0.1)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 215, 0, 0.2)',
                },
              }}
            />
          </MotionBox>

          {/* Main Title with Enhanced Styling */}
          <MotionBox
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            sx={{ mb: 4 }}
          >
            <Typography
              variant={isMobile ? 'h2' : 'h1'}
              component="h1"
              sx={{
                fontWeight: 900,
                fontSize: isMobile ? '3rem' : '4.5rem',
                lineHeight: 1.1,
                background: 'linear-gradient(135deg, #ffd700 0%, #4a90e2 50%, #ffd700 100%)',
                backgroundSize: '200% 200%',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                animation: 'gradient 3s ease infinite',
                '@keyframes gradient': {
                  '0%': { backgroundPosition: '0% 50%' },
                  '50%': { backgroundPosition: '100% 50%' },
                  '100%': { backgroundPosition: '0% 50%' },
                },
                textShadow: '0 0 40px rgba(255, 215, 0, 0.3)',
              }}
            >
              Level Up Your Life
            </Typography>
            <Typography
              variant="h6"
              sx={{
                mt: 1,
                color: 'primary.main',
                fontWeight: 600,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                fontSize: '0.9rem',
              }}
            >
              The Ultimate Personal Development RPG
            </Typography>
          </MotionBox>

          {/* Enhanced Description */}
          <MotionBox
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            sx={{ mb: 6 }}
          >
            <Typography
              variant={isMobile ? 'body1' : 'h5'}
              sx={{
                color: 'text.secondary',
                maxWidth: '700px',
                mx: 'auto',
                lineHeight: 1.6,
                fontWeight: 400,
                fontSize: isMobile ? '1.1rem' : '1.3rem',
              }}
            >
              Transform your personal development journey with our{' '}
              <Box component="span" sx={{ color: 'primary.main', fontWeight: 600 }}>
                talent tree system
              </Box>{' '}
              inspired by World of Warcraft. Strategically allocate your weekly time across health, skills, and goals to{' '}
              <Box component="span" sx={{ color: 'secondary.main', fontWeight: 600 }}>
                unlock your full potential
              </Box>.
            </Typography>
          </MotionBox>

          {/* Enhanced Action Buttons */}
          <MotionBox
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap', mb: 4 }}>
              <Button
                variant="contained"
                size="large"
                component={Link}
                to="/register"
                sx={{
                  px: 6,
                  py: 2,
                  fontSize: '1.2rem',
                  fontWeight: 'bold',
                  borderRadius: '50px',
                  background: 'linear-gradient(135deg, #ffd700 0%, #ffed4e 100%)',
                  color: 'black',
                  boxShadow: '0 8px 32px rgba(255, 215, 0, 0.4)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #ffed4e 0%, #ffd700 100%)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 12px 40px rgba(255, 215, 0, 0.6)',
                  },
                }}
                startIcon={<EmojiEvents />}
              >
                Start Your Journey
              </Button>
              <Button
                variant="outlined"
                size="large"
                component={Link}
                to="/pricing"
                sx={{
                  px: 6,
                  py: 2,
                  fontSize: '1.2rem',
                  fontWeight: 'bold',
                  borderRadius: '50px',
                  borderWidth: '2px',
                  borderColor: 'primary.main',
                  color: 'primary.main',
                  backgroundColor: 'rgba(255, 215, 0, 0.05)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: 'primary.main',
                    backgroundColor: 'rgba(255, 215, 0, 0.15)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 32px rgba(255, 215, 0, 0.2)',
                  },
                }}
                startIcon={<TrendingUp />}
              >
                View Pricing
              </Button>
            </Box>

            {/* Trust Indicators */}
            <Box sx={{ display: 'flex', gap: 4, justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Star sx={{ color: '#ffd700', fontSize: '1.2rem' }} />
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  4.9/5 Rating
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircle sx={{ color: 'success.main', fontSize: '1.2rem' }} />
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  10,000+ Users
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Security sx={{ color: 'info.main', fontSize: '1.2rem' }} />
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  100% Secure
                </Typography>
              </Box>
            </Box>
          </MotionBox>
        </MotionBox>

        {/* Features Section */}
        <MotionBox
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          sx={{ mb: 8 }}
        >
          <Typography
            variant="h2"
            component="h2"
            sx={{ textAlign: 'center', mb: 6, fontWeight: 'bold' }}
          >
            Why Choose Health-Tree?
          </Typography>
          <Grid container spacing={4}>
            {features.map((feature, index) => (
              <Grid item xs={12} md={6} lg={4} key={index}>
                <MotionCard
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      transition: 'transform 0.3s ease-in-out',
                    },
                  }}
                >
                  <CardContent sx={{ flexGrow: 1, textAlign: 'center', p: 3 }}>
                    <Box sx={{ mb: 2 }}>{feature.icon}</Box>
                    <Typography variant="h6" component="h3" sx={{ mb: 2, fontWeight: 'bold' }}>
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {feature.description}
                    </Typography>
                  </CardContent>
                </MotionCard>
              </Grid>
            ))}
          </Grid>
        </MotionBox>

        {/* Pricing Tiers Preview */}
        <MotionBox
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          sx={{ textAlign: 'center' }}
        >
          <Typography
            variant="h2"
            component="h2"
            sx={{ mb: 6, fontWeight: 'bold' }}
          >
            Choose Your Plan
          </Typography>
          <Grid container spacing={4} justifyContent="center">
            <Grid item xs={12} md={6} lg={4}>
              <MotionCard
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.7 }}
                sx={{ p: 3, textAlign: 'center' }}
              >
                <Typography variant="h4" sx={{ mb: 2, fontWeight: 'bold' }}>
                  Free
                </Typography>
                <Typography variant="h3" sx={{ mb: 3, color: 'primary.main' }}>
                  $0<Typography component="span" variant="h6">/month</Typography>
                </Typography>
                <Box sx={{ mb: 3 }}>
                  <Chip label="5 Goals" sx={{ m: 0.5 }} />
                  <Chip label="1 Talent Tree" sx={{ m: 0.5 }} />
                  <Chip label="Basic Analytics" sx={{ m: 0.5 }} />
                </Box>
                <Button
                  variant="outlined"
                  fullWidth
                  component={Link}
                  to="/register"
                  sx={{ py: 1.5 }}
                >
                  Get Started
                </Button>
              </MotionCard>
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <MotionCard
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.8 }}
                sx={{
                  p: 3,
                  textAlign: 'center',
                  border: '2px solid',
                  borderColor: 'primary.main',
                  position: 'relative',
                }}
              >
                <Chip
                  label="Most Popular"
                  sx={{
                    position: 'absolute',
                    top: -12,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    backgroundColor: 'primary.main',
                    color: 'black',
                    fontWeight: 'bold',
                  }}
                />
                <Typography variant="h4" sx={{ mb: 2, fontWeight: 'bold' }}>
                  Premium
                </Typography>
                <Typography variant="h3" sx={{ mb: 3, color: 'primary.main' }}>
                  $9.99<Typography component="span" variant="h6">/month</Typography>
                </Typography>
                <Box sx={{ mb: 3 }}>
                  <Chip label="Unlimited Goals" sx={{ m: 0.5 }} />
                  <Chip label="Custom Talent Trees" sx={{ m: 0.5 }} />
                  <Chip label="Advanced Analytics" sx={{ m: 0.5 }} />
                  <Chip label="Priority Support" sx={{ m: 0.5 }} />
                </Box>
                <Button
                  variant="contained"
                  fullWidth
                  component={Link}
                  to="/register"
                  sx={{ py: 1.5 }}
                >
                  Upgrade Now
                </Button>
              </MotionCard>
            </Grid>
          </Grid>
        </MotionBox>
      </Container>
      
      {/* Temporary Debug Component */}
      <DebugAuth />
    </Box>
  );
};

export default LandingPage; 